import { createPagedListQuery, createUrl } from '../helpers'
import { DEFAULT_REQUEST_OPTIONS, DEFAULT_PAGED_LIST_OPTIONS } from '../config'
import type { ApiClient } from '../../index'
import type {
  DeepPartial,
  Id,
  PathOptions,
  Path,
  PagedListQuery,
  PagedListRequestOptionsNoQuery,
  PaginationType,
  PagedListOptions,
  ClassicPaginationMeta,
  LastIdPaginationMeta
} from '../types'

export interface Data<U, Meta> {
  items: U[]
  meta: Meta | null
  url: string
  query: PagedListQuery | null
  isLastPage: boolean
}

export const pagedListWrapper =
  <
    K extends string,
    T extends { [key in K]: Id },
    PathO extends PathOptions = PathOptions
  >() =>
  <PaginationT extends PaginationType | undefined = undefined>(
    apiClient: ApiClient,
    receivedOptions: PagedListOptions<K, PathO, PaginationT>
  ) => {
    type ResponseMeta = PaginationT extends undefined
      ? ClassicPaginationMeta
      : PaginationT extends 'lastId'
        ? LastIdPaginationMeta<T[K]>
        : ClassicPaginationMeta

    const baseOptions = (() => {
      const mergedOptions = Object.assign(
        {},
        DEFAULT_PAGED_LIST_OPTIONS,
        receivedOptions
      )

      delete mergedOptions.requestOptions
      return mergedOptions as Omit<typeof mergedOptions, 'requestOptions'>
    })()

    const baseRequestOptions = (() => {
      const mergedOptions = Object.assign(
        {},
        DEFAULT_REQUEST_OPTIONS,
        DEFAULT_PAGED_LIST_OPTIONS.requestOptions,
        receivedOptions.requestOptions
      )

      delete mergedOptions.query
      return mergedOptions as Omit<typeof mergedOptions, 'query'>
    })()

    const baseQuery = Object.assign(
      {},
      DEFAULT_REQUEST_OPTIONS.query,
      DEFAULT_PAGED_LIST_OPTIONS.requestOptions?.query,
      receivedOptions.requestOptions?.query
    )

    const idKey = baseOptions.idKey

    return {
      idKey,

      getItems: async <U extends Pick<T, K> & DeepPartial<Omit<T, K>> = T>(
        requestQuery: PagedListQuery = {},
        pathOptions?: PathO,
        requestOptions: PagedListRequestOptionsNoQuery = {}
      ) => {
        const options = Object.assign({}, baseRequestOptions, requestOptions)
        const paginationQuery =
          baseOptions.paginationType === 'lastId' ? {} : { page: 1 }
        const mergedQuery = Object.assign(
          {},
          baseQuery,
          paginationQuery,
          requestQuery
        )

        const query = createPagedListQuery(baseOptions, options, mergedQuery)
        const url = createUrl(
          baseOptions.path as Path,
          requestQuery,
          pathOptions ?? {}
        )

        const response = await apiClient.get<
          | {
              items: U[]
              _meta: ResponseMeta
            }
          | U[]
          | null
        >(url, { query }, { cache: options.cache })

        const data: Data<U, ResponseMeta> = {
          items: [],
          meta: null,
          url,
          query: mergedQuery,
          isLastPage: true
        }

        if (response && !Array.isArray(response) && options.pagination) {
          data.items = response.items
          data.meta = response._meta
          data.isLastPage =
            baseOptions.paginationType === 'lastId'
              ? (response._meta as LastIdPaginationMeta).lastId === null
              : (response._meta as ClassicPaginationMeta).pageCount < 2
        } else if (Array.isArray(response)) {
          data.items = response
        }

        return {
          ...data,
          _response: response
        }
      },

      getMoreItems: async <U extends Pick<T, K> & DeepPartial<Omit<T, K>> = T>(
        data: Data<U, ResponseMeta>,
        requestOptions: PagedListRequestOptionsNoQuery = {}
      ) => {
        if (data.isLastPage || !data.query) {
          return
        }

        const options = Object.assign({}, baseRequestOptions, requestOptions)

        const lastId = data.items[data.items.length - 1][idKey]
        const paginationQuery =
          baseOptions.paginationType === 'lastId'
            ? { last_id: lastId }
            : { page: data.query.page + 1 }
        const mergedQuery = Object.assign({}, data.query, paginationQuery)

        const query = createPagedListQuery(baseOptions, options, mergedQuery)

        const response = await apiClient.get<{
          items: U[]
          _meta: ResponseMeta
        }>(data.url, { query }, { cache: options.cache })

        const newData: Data<U, ResponseMeta> = {
          items: data.items.concat(response.items),
          meta: response._meta,
          url: data.url,
          query: mergedQuery,
          isLastPage:
            baseOptions.paginationType === 'lastId'
              ? (response._meta as LastIdPaginationMeta).lastId === null
              : (response._meta as ClassicPaginationMeta).currentPage ===
                (response._meta as ClassicPaginationMeta).pageCount
        }

        return {
          ...newData,
          _response: response
        }
      }
    }
  }

export type PagedListWrapperPublicInterface = ReturnType<
  ReturnType<typeof pagedListWrapper>
>
